import type { PropertyType, Location } from '../../../../shared/model/tsTypes';
import { getPropertyTypesFromIds } from '../../../../shared/model/constants/propertyTypes';
import {
  carSpacesFilterOptions,
  nabersEnergyRatingFilterOptions,
} from '../constants/filtersOptions';
import type {
  ExtendedMultiLocationSuggestOption,
  FilterOptionType,
  RefinementsType,
  SelectedFiltersOptionsType,
} from '../../../../shared/model/constants/filterTypes';
import { getTenureTypeByKey } from '../../../../shared/model/constants/tenureType';

export const DEFAULT_PROPERTY_TYPE_FILTER_OPTION: never[] = [];
export const DEFAULT_SELECT_FILTER_OPTION: FilterOptionType = { label: 'Any', value: '' };
export const DEFAULT_TENURE_TYPE_FITLER_OPTION: string = 'any';
export const DEFAULT_SILDER_INPUT_FILTER_OPTION: Array<number> = [0, 0];
export const DEFAULT_SELECTED_LOCATIONS: Array<ExtendedMultiLocationSuggestOption> = [];
export const DEFAULT_FILTERS_REFINEMENTS_OPTIONS = {
  propertyTypes: DEFAULT_PROPERTY_TYPE_FILTER_OPTION,
  tenureType: DEFAULT_TENURE_TYPE_FITLER_OPTION,
  priceRange: DEFAULT_SILDER_INPUT_FILTER_OPTION,
  floorAreaRange: DEFAULT_SILDER_INPUT_FILTER_OPTION,
  landAreaRange: DEFAULT_SILDER_INPUT_FILTER_OPTION,
  numParkingSpaces: DEFAULT_SELECT_FILTER_OPTION,
  energyEfficiency: DEFAULT_SELECT_FILTER_OPTION,
  keywords: '',
  includeSurroundingSuburbs: true,
};

const capitalize = (s: string): string => s && s[0].toUpperCase() + s.slice(1);

const locationObjToDisplayText = (location: Location) => {
  const { locality, subdivision, postcode } = location;
  const capitalizedLocality = locality
    ?.split(' ')
    .map((t) => capitalize(t))
    .join(' ');
  const displayLocationText = `${capitalizedLocality}${
    subdivision !== undefined ? `, ${subdivision?.toUpperCase()}` : ''
  } ${postcode ?? ''}`.trimEnd();
  return displayLocationText;
};

const mapUrlLocalitiesToSearchLocationsOption = (
  localities: Array<Location>,
): Array<ExtendedMultiLocationSuggestOption> => {
  if (localities.length > 0) {
    return localities.map((location: Location) => ({
      source: {
        state: location.subdivision,
        name: location.locality,
        postcode: location.postcode,
      },
      display: {
        text: locationObjToDisplayText(location),
      },
    }));
  }
  return [];
};

const mapSearchLocationToLocality = (
  searchLocations: Array<ExtendedMultiLocationSuggestOption>,
): Array<Location> => {
  if (searchLocations.length === 0) {
    return [];
  }
  return searchLocations.map((searchLocation: ExtendedMultiLocationSuggestOption) => {
    if (searchLocation.source && Object.keys(searchLocation.source).length !== 0) {
      return {
        locality: searchLocation.source?.name,
        postcode: searchLocation.source?.postcode,
        subdivision: searchLocation.source?.state,
      };
    }
    return {
      locality: searchLocation.display.text,
    };
  });
};

const getIncludeSurroundingSuburbs = (
  includePropertiesWithin: 'includesurrounding' | 'excludesurrounding' | undefined,
): boolean | undefined => {
  if (includePropertiesWithin === 'excludesurrounding') {
    return false;
  }

  return true;
};

const includeSurroundingSuburbsToText = (
  includeSurroundingSuburbs: boolean | undefined,
): 'includesurrounding' | 'excludesurrounding' =>
  (includeSurroundingSuburbs ? 'includesurrounding' : 'excludesurrounding');

export const mapInitialRefinementsToFilterOptions = (
  initialRefinements: RefinementsType,
): SelectedFiltersOptionsType => {
  const {
    localities,
    channelObject,
    propertyTypes,
    tenureTypeObject,
    minPrice,
    maxPrice,
    minFloorArea,
    maxFloorArea,
    minSiteArea,
    maxSiteArea,
    numParkingSpaces,
    energyEfficiency,
    keywords,
    includePropertiesWithin,
    boundingBox,
  } = initialRefinements;

  return {
    searchLocations:
      localities !== undefined ? mapUrlLocalitiesToSearchLocationsOption(localities) : [],
    channel: channelObject,
    propertyTypes:
      propertyTypes !== undefined
        ? propertyTypes.map((propertyType: PropertyType) => propertyType.id)
        : DEFAULT_PROPERTY_TYPE_FILTER_OPTION,
    tenureType: tenureTypeObject?.key,
    priceRange: [minPrice ?? 0, maxPrice ?? 0],
    floorAreaRange: [minFloorArea ?? 0, maxFloorArea ?? 0],
    landAreaRange: [minSiteArea ?? 0, maxSiteArea ?? 0],
    numParkingSpaces:
      numParkingSpaces !== undefined
        ? carSpacesFilterOptions.find(
          (carSpacesOption: FilterOptionType) =>
            carSpacesOption.value === numParkingSpaces?.toString(),
        ) ?? DEFAULT_SELECT_FILTER_OPTION
        : DEFAULT_SELECT_FILTER_OPTION,
    energyEfficiency:
      energyEfficiency !== undefined
        ? nabersEnergyRatingFilterOptions.find(
          (energyOption: FilterOptionType) => energyOption.value === energyEfficiency?.toString(),
        ) ?? DEFAULT_SELECT_FILTER_OPTION
        : DEFAULT_SELECT_FILTER_OPTION,
    keywords: keywords ?? '',
    includeSurroundingSuburbs: getIncludeSurroundingSuburbs(includePropertiesWithin),
    boundingBox,
  };
};

const getLocalities = (searchLocations?: ExtendedMultiLocationSuggestOption[]) =>
  (searchLocations ? mapSearchLocationToLocality(searchLocations) : undefined);

export const mapFilterOptionsToRefinements = (
  filterOptions: SelectedFiltersOptionsType,
  refinements?: RefinementsType,
): RefinementsType => {
  const {
    channel,
    propertyTypes,
    tenureType,
    priceRange,
    floorAreaRange,
    landAreaRange,
    energyEfficiency,
    numParkingSpaces,
    keywords,
    searchLocations,
    includeSurroundingSuburbs,
    boundingBox,
  } = filterOptions;

  const localities = getLocalities(searchLocations);

  return {
    ...refinements,
    channelObject: channel,
    propertyTypes: getPropertyTypesFromIds(propertyTypes),
    tenureTypeObject: getTenureTypeByKey(tenureType),
    minPrice: priceRange[0],
    maxPrice: priceRange[1],
    minFloorArea: floorAreaRange[0],
    maxFloorArea: floorAreaRange[1],
    minSiteArea: landAreaRange[0],
    maxSiteArea: landAreaRange[1],
    numParkingSpaces: Number(numParkingSpaces.value),
    energyEfficiency: Number(energyEfficiency.value),
    localities,
    includePropertiesWithin: includeSurroundingSuburbsToText(includeSurroundingSuburbs),
    keywords,
    boundingBox: !localities || localities.length === 0 ? boundingBox : undefined,
  };
};
